import { type DirectiveBinding, type Directive } from 'vue'
import { assertNotNull } from '@/helpers/typing'

interface ClickOutSideElement extends HTMLElement {
  clickOutsideEvent?: (e: MouseEvent) => void
}

export const clickOutside: Directive = {
  created: function (el: ClickOutSideElement, binding: DirectiveBinding): void {
    el.clickOutsideEvent = function (event): void {
      // Here I check that click was outside the el and his children
      if (!(el === event.target || el.contains(event.target as Node))) {
        // And if it did, call method provided in attribute value
        // Vue 3 version: https://stackoverflow.com/a/63910611/4612064
        binding.value(event, el)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted: function (el: ClickOutSideElement): void {
    document.body.removeEventListener(
      'click',
      assertNotNull(el.clickOutsideEvent),
    )
  },
}

type OnVisibleHandler = (entry: IntersectionObserverEntry) => void

interface OnVisibleElement extends HTMLElement {
  onVisibleObserver?: IntersectionObserver
}

// When 25% of the element is visible, run the handler for vOnVisible.
const ON_VISIBLE_THRESHOLD = 0.25

/**
 * Call a handler function when at least 25 percent of an element is visible.
 */
export const vOnVisible: Directive<OnVisibleElement, OnVisibleHandler, 'once'> =
  {
    mounted(
      el: OnVisibleElement,
      binding: DirectiveBinding<OnVisibleHandler, 'once'>,
    ): void {
      const handler = binding.value
      const triggerOnlyOnce = Boolean(binding.modifiers.once)
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (
            !entry ||
            el.onVisibleObserver !== observer ||
            !entry.isIntersecting ||
            // This callback is called when we first call observer.observe(),
            // even if the ratio is below the threshold, so we need to check
            // the ratio here.
            entry.intersectionRatio < ON_VISIBLE_THRESHOLD
          ) {
            return
          }

          if (triggerOnlyOnce) {
            // Clean up before the handler runs so that if it throws we do not
            // leave the observer active.
            observer.disconnect()
            delete el.onVisibleObserver
          }

          handler(entry)
        },
        { threshold: ON_VISIBLE_THRESHOLD },
      )

      el.onVisibleObserver = observer
      observer.observe(el)
    },

    unmounted(el: OnVisibleElement): void {
      el.onVisibleObserver?.disconnect()
      delete el.onVisibleObserver
    },
  }
